import React, { useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import Button from './Button'
import Input from './Input'
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_CHANGED,
  PASSWORD_TOO_SHORT,
  PASSWORDS_DO_NOT_MATCH,
  emailChangeRequested,
  translateAuthError
} from '../authMessages'
import '../styles/token-popup.css'

// Учётные данные аккаунта: пароль и адрес входа. Точка входа — адрес
// пользователя в шапке. Две вкладки, а не две формы подряд: у каждой операции
// свой текущий пароль, и два одинаковых поля на экране путали бы.
export default function CredentialsPopup ({ open, onClose, user }) {
  const [tab, setTab] = useState('password') // 'password' | 'email'

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [emailPassword, setEmailPassword] = useState('')
  const [newEmail, setNewEmail] = useState('')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(null)

  const ref = useRef(null)
  const previousFocus = useRef(null)

  useEffect(() => {
    if (!open) return
    setTab('password')
    setCurrent('')
    setNext('')
    setRepeat('')
    setEmailPassword('')
    setNewEmail('')
    setError(null)
    setDone(null)
    setSaving(false)
  }, [open])

  useEffect(() => {
    if (!open) return
    function handleMouseDown (e) {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open, onClose])

  // Диалог получает фокус, удерживает его внутри и возвращает на кнопку,
  // которая его открыла — как в попапе токена.
  useEffect(() => {
    if (!open) return
    previousFocus.current = document.activeElement
    ref.current?.focus()

    function handleKey (e) {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab' || !ref.current) return

      const focusable = Array.from(ref.current.querySelectorAll(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'
      ))
      if (focusable.length === 0) {
        e.preventDefault()
        ref.current.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('keydown', handleKey)
      previousFocus.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  function switchTab (nextTab) {
    setTab(nextTab)
    setError(null)
    setDone(null)
  }

  async function handlePasswordSubmit (e) {
    e?.preventDefault?.()
    if (saving) return

    // Длина и совпадение проверяются до запроса: сервер их тоже проверяет, но
    // гонять сеть ради того, что видно в форме, незачем.
    if (next.length < MIN_PASSWORD_LENGTH) {
      setError(PASSWORD_TOO_SHORT)
      return
    }
    if (next !== repeat) {
      setError(PASSWORDS_DO_NOT_MATCH)
      return
    }

    setSaving(true)
    setError(null)
    try {
      await api.changePassword(current, next)
      setDone(PASSWORD_CHANGED)
    } catch (err) {
      setError(translateAuthError(err))
    } finally {
      setSaving(false)
    }
  }

  async function handleEmailSubmit (e) {
    e?.preventDefault?.()
    if (saving) return

    const address = newEmail.trim()
    setSaving(true)
    setError(null)
    try {
      await api.changeEmail(emailPassword, address)
      setDone(emailChangeRequested(address))
    } catch (err) {
      setError(translateAuthError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='token-popup__overlay'>
      <section
        aria-labelledby='account-credentials-title'
        aria-modal='true'
        className='token-popup'
        ref={ref}
        role='dialog'
        tabIndex='-1'
      >
        <h2 className='token-popup__title' id='account-credentials-title'>Учётные данные</h2>

        <div className='token-popup__tabs'>
          <button
            aria-pressed={tab === 'password'}
            className='token-popup__tab'
            onClick={() => switchTab('password')}
            type='button'
          >
            Пароль
          </button>
          <button
            aria-pressed={tab === 'email'}
            className='token-popup__tab'
            onClick={() => switchTab('email')}
            type='button'
          >
            Адрес входа
          </button>
        </div>

        {done
          ? (
            <div className='token-popup__content'>
              <div className='token-popup__banner' role='status'>{done}</div>
              <div className='token-popup__actions'>
                <Button onClick={onClose}>Закрыть</Button>
              </div>
            </div>
            )
          : tab === 'password'
            ? (
              <form className='token-popup__content' onSubmit={handlePasswordSubmit}>
                <div className='token-popup__field'>
                  <label className='token-popup__label' htmlFor='current-password'>Текущий пароль</label>
                  <Input
                    autoComplete='current-password'
                    autoFocus
                    className='token-popup__input'
                    id='current-password'
                    onChange={e => setCurrent(e.target.value)}
                    placeholder='Действующий пароль'
                    type='password'
                    value={current}
                  />
                </div>
                <div className='token-popup__field'>
                  <label className='token-popup__label' htmlFor='new-password'>Новый пароль</label>
                  <Input
                    autoComplete='new-password'
                    className='token-popup__input'
                    id='new-password'
                    onChange={e => setNext(e.target.value)}
                    placeholder='Минимум 8 символов'
                    type='password'
                    value={next}
                  />
                </div>
                <div className='token-popup__field'>
                  <label className='token-popup__label' htmlFor='repeat-new-password'>Повторите новый пароль</label>
                  <Input
                    autoComplete='new-password'
                    className='token-popup__input'
                    id='repeat-new-password'
                    onChange={e => setRepeat(e.target.value)}
                    placeholder='Ещё раз новый пароль'
                    type='password'
                    value={repeat}
                  />
                </div>

                {error && <div className='token-popup__error' role='alert'>{error}</div>}

                <div className='token-popup__actions'>
                  <Button loading={saving} type='submit'>Сменить пароль</Button>
                  <Button disabled={saving} onClick={onClose} variant='ghost'>Отмена</Button>
                </div>
              </form>
              )
            : (
              <form className='token-popup__content' onSubmit={handleEmailSubmit}>
                {user?.email && (
                  <div className='token-popup__label'>Текущий адрес: {user.email}</div>
                )}

                <div className='token-popup__field'>
                  <label className='token-popup__label' htmlFor='email-current-password'>Текущий пароль</label>
                  <Input
                    autoComplete='current-password'
                    autoFocus
                    className='token-popup__input'
                    id='email-current-password'
                    onChange={e => setEmailPassword(e.target.value)}
                    placeholder='Действующий пароль'
                    type='password'
                    value={emailPassword}
                  />
                </div>

                <div className='token-popup__field'>
                  <label className='token-popup__label' htmlFor='new-email'>Новый адрес входа</label>
                  <Input
                    autoComplete='email'
                    className='token-popup__input'
                    id='new-email'
                    onChange={e => setNewEmail(e.target.value)}
                    placeholder='you@example.com'
                    type='email'
                    value={newEmail}
                  />
                  <div className='token-popup__hint'>
                    На новый адрес придёт письмо со ссылкой подтверждения. До
                    подтверждения вход остаётся по текущему адресу.
                  </div>
                </div>

                {error && <div className='token-popup__error' role='alert'>{error}</div>}

                <div className='token-popup__actions'>
                  <Button loading={saving} type='submit'>Сменить адрес</Button>
                  <Button disabled={saving} onClick={onClose} variant='ghost'>Отмена</Button>
                </div>
              </form>
              )}
      </section>
    </div>
  )
}
